import React from "react";
import { Box, Drawer, Typography, Stack, Avatar, CircularProgress, alpha } from "@mui/material";
import { HourglassEmpty as HourglassIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { DrawerHeader, ActionButton } from "../../../components/atoms";
import EmptyState from "../../../components/atoms/EmptyState";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import useTerms from "../../../hooks/useTerms";
import { fullName, initialsOf, avatarTone } from "./patientListFormat";

const daysLeft = (expiresAt) =>
  expiresAt ? Math.max(0, Math.ceil((new Date(expiresAt) - Date.now()) / (24 * 60 * 60 * 1000))) : null;

/**
 * Hasta-klinik bağlantısı için bekleyen talepler — sağdan açılan drawer.
 * Hasta talebi mobilden onaylayınca takip başlar ve hasta listeye girer;
 * 7 gün içinde yanıt yoksa talep kendiliğinden kapanır.
 */
export default function PendingRequestsDrawer({ open, onClose, links, isLoading, canCancel, onCancel, cancelling }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<HourglassIcon />}
        bgcolor={COLORS.warning}
        title="Bekleyen Talepler"
        subtitle={`${terms.patientGen} onayını bekleyen bağlantı talepleri`}
        onClose={onClose}
      />

      <Box sx={{ px: 3, py: 1.5, bgcolor: COLORS.warningLightest, borderBottom: `1px solid ${COLORS.slate200}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600, lineHeight: 1.5 }}>
          {terms.patient} talebi uygulamadan onaylayınca takip başlar ve {terms.patientLower} listenize eklenir. 7 gün içinde yanıt gelmezse talep
          kendiliğinden kapanır.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: COLORS.slate300 }} />
          </Box>
        ) : links.length === 0 ? (
          <Box sx={{ pt: 4 }}>
            <EmptyState
              variant="illustrated-sm"
              icon={<HourglassIcon />}
              title="Bekleyen talep yok"
              description={`Uygulamayı kullanan bir ${terms.patientAccLower} eklediğinizde, ${terms.patientLower} onaylayana kadar talep burada görünür.`}
            />
          </Box>
        ) : (
          <Stack>
            {links.map((l) => {
              const remaining = daysLeft(l.expiresAt);
              const tone = avatarTone(l.patient);
              return (
                <Box
                  key={l._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 3,
                    py: 1.5,
                    borderBottom: `1px solid ${COLORS.slate100}`,
                  }}
                >
                  <Avatar sx={{ width: 34, height: 34, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
                    {initialsOf(l.patient)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
                      {fullName(l.patient)}
                    </Typography>
                    <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                      {new Date(l.requestedAt).toLocaleDateString("tr-TR")}
                      {remaining !== null ? ` · ${remaining} gün kaldı` : ""}
                    </Typography>
                  </Box>
                  {canCancel && (
                    <ActionButton
                      variant="delete"
                      palette={palette}
                      compact
                      label="İptal"
                      icon={<CancelIcon sx={{ fontSize: 14 }} />}
                      disabled={cancelling}
                      onClick={() => onCancel(l._id)}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
